import React, { ChangeEvent, forwardRef, ReactNode, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { parsePhoneNumber } from 'react-phone-number-input';

import { Input } from '../';
import AvatarUploader, { AvatarUploaderProps } from '../AvatarUploader/AvatarUploader';
import { DropDownProps } from '../Dropdown/Dropdown';
import { InputProps } from '../Input';
import OrganizationsDropdown, { OrganizationsDropdownProps } from '../OrganizationsDropdown/OrganizationsDropdown';
import { PhoneInput, PhoneInputProps } from '../PhoneInput/PhoneInput';
import PlacesAutocomplete, { PlacesAutocompleteProps } from '../PlacesAutocomplete/PlacesAutocomplete';
import { StyledDropdown } from './Form.styles';

interface CommonFormFieldProps {
  dataKey: string;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  errorRequired?: string;
}

export type InputFormProps = CommonFormFieldProps & InputProps;
export type DropdownFormProps = CommonFormFieldProps & DropDownProps;
type OrganizationsFormProps = CommonFormFieldProps & OrganizationsDropdownProps;

type PlacesFormProps = CommonFormFieldProps & PlacesAutocompleteProps;
interface AvatarFormProps extends CommonFormFieldProps, Omit<AvatarUploaderProps, 'onUploadFinish'> {
  onUploadFinish?: (url: string) => void;
}
type PhoneFormProp = CommonFormFieldProps & PhoneInputProps;

type FormRenderType = {
  input: (props: InputFormProps) => ReactNode;
  dropdown: (props: DropdownFormProps) => ReactNode;
  places: (props: PlacesFormProps) => ReactNode;
  avatar: (props: AvatarFormProps) => ReactNode;
  phone: (props: PhoneFormProp) => ReactNode;
  organizations: (props: OrganizationsFormProps) => ReactNode;
  data: FormData;
  isDataChanged: () => boolean;
  isRequiredDataFilled: boolean;
};

export type FormData = Record<string, any>;
type ErrorsData = Record<string, string>;

interface FormProps {
  data?: FormData;
  validateFn?: (formData: FormData, errors: ErrorsData) => boolean;
  onSubmit?: (formData: FormData) => void;
  children: (renderProps: FormRenderType) => ReactNode;
}

const Form = forwardRef(({ children, data, validateFn, onSubmit }: FormProps, ref) => {
  const [localData, setLocalData] = useState<FormData>(data || {});
  const [initialData, setInitialData] = useState<FormData>(data || {});
  const [errors, setErrors] = useState<ErrorsData>({});
  const [isRequiredDataFilled, setIsRequiredDataFilled] = useState<boolean>(false);
  const [cancelCounter, setCancelCounter] = useState(0);
  const fields: CommonFormFieldProps[] = useMemo(() => [], []);

  useImperativeHandle(ref, () => ({
    submit: () => {
      handleSubmit();
    },
    cancel: () => {
      setLocalData(data || {});
      setErrors({});
      setCancelCounter(cancelCounter + 1);
    },
  }));

  const isDataChanged = () => {
    for (const key in data) {
      if (data[key] !== localData[key]) {
        return true;
      }
    }

    return false;
  };

  useEffect(() => {
    const fieldsRequired = fields.filter((item) => item.required);
    for (const field of fieldsRequired) {
      const key = field.dataKey;
      if (localData[key] === undefined || localData[key] === '') {
        return setIsRequiredDataFilled(false);
      }
    }
    return setIsRequiredDataFilled(true);
  }, [fields, localData]);

  const updateLocalDataIfNeeded = () => {
    let isChanged = false;
    for (const key in data) {
      if (data[key] !== initialData[key]) {
        localData[key] = data[key];
        isChanged = true;
      }
    }

    if (isChanged) {
      setInitialData(data);
    }
  };

  updateLocalDataIfNeeded();

  const validateRequiredFields = () => {
    const errorsRequired = {};
    const fieldsRequired = fields.filter((item) => item.required);
    for (const field of fieldsRequired) {
      const key = field.dataKey;
      if (localData[key] === undefined || localData[key] === '') {
        errorsRequired[key] = field.errorRequired;
      }
    }

    return errorsRequired;
  };

  const validate = () => {
    const localErrors = validateRequiredFields();
    if (Object.keys(localErrors).length > 0) {
      setErrors(localErrors);
      return false;
    }

    if (validateFn) {
      const valid = validateFn(localData, localErrors);
      setErrors(localErrors);
      return valid;
    }

    return true;
  };

  const handleSubmit = () => {
    if (!validate()) {
      return;
    }

    if (onSubmit) onSubmit(localData);
  };

  const updateValue = (key: string, value: any) => {
    setLocalData({ ...localData, [key]: value });
    setErrors({ ...errors, [key]: undefined });
  };

  const addFormFieldIfNeeded = (props: CommonFormFieldProps) => {
    if (fields.some((item) => item.dataKey === props.dataKey)) return;
    fields.push(props);
  };

  const input = (props: InputFormProps) => {
    addFormFieldIfNeeded(props);

    const { dataKey, ...rest } = props;
    const InputComponent = props.type === 'password' ? Input.Password : Input;
    return (
      <InputComponent
        key={dataKey}
        fullWidth
        value={localData[dataKey] || ''}
        error={errors[dataKey]}
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          updateValue(dataKey, e.target.value);
        }}
        onKeyPress={(event) => {
          if (event.key === 'Enter') {
            handleSubmit();
          }
        }}
        {...rest}
      />
    );
  };

  const dropdown = (props: DropdownFormProps) => {
    addFormFieldIfNeeded(props);

    const { dataKey, label, onSelectionChange, ...rest } = props;
    return (
      <StyledDropdown
        key={dataKey}
        title={label}
        error={errors[dataKey]}
        selectedIndex={props.selectedIndex >= 0 ? props.selectedIndex : localData[dataKey]}
        onSelectionChange={(selection) => {
          updateValue(dataKey, selection);
          onSelectionChange?.(selection);
        }}
        {...rest}
      />
    );
  };

  const places = (props: PlacesFormProps) => {
    addFormFieldIfNeeded(props);

    const { dataKey, onSelect, ...rest } = props;
    return (
      <PlacesAutocomplete
        key={`places-${dataKey}-${cancelCounter}`}
        error={errors[dataKey]}
        onSelect={(place) => {
          updateValue(dataKey, place);
          onSelect?.(place);
        }}
        {...rest}
      />
    );
  };

  const avatar = (props: AvatarFormProps) => {
    addFormFieldIfNeeded(props);

    const { dataKey, onUploadFinish, ...rest } = props;
    return (
      <AvatarUploader
        key={dataKey}
        imageUrl={localData[dataKey]}
        onUploadFinish={(url: string) => {
          updateValue(dataKey, url);
          onUploadFinish?.(url);
        }}
        {...rest}
      />
    );
  };

  const phone = (props: PhoneFormProp) => {
    addFormFieldIfNeeded(props);

    const { dataKey, defaultValue, ...rest } = props;
    const value = localData[dataKey] as string;
    return (
      <PhoneInput
        key={`phone-${dataKey}-${cancelCounter}`}
        error={errors[dataKey]}
        value={parsePhoneNumber(value || '')?.number}
        onChange={(phone) => {
          updateValue(dataKey, phone);
        }}
        {...rest}
      />
    );
  };

  const organizations = (props: OrganizationsFormProps) => {
    addFormFieldIfNeeded(props);
    const { dataKey, label, onSelectionChange, ...rest } = props;
    return (
      <OrganizationsDropdown
        key={dataKey}
        title={label}
        error={errors[dataKey]}
        selectedIndex={props.selectedIndex >= 0 ? props.selectedIndex : localData[dataKey]}
        onSelectionChange={(selection, org) => {
          updateValue(dataKey, org);
          onSelectionChange?.(selection, org);
        }}
        {...rest}
      />
    );
  };

  return (
    <>
      {children({
        input,
        dropdown,
        places,
        avatar,
        phone,
        organizations,
        data: localData,
        isDataChanged,
        isRequiredDataFilled,
      })}
    </>
  );
});

export default Form;
